import React, { useState, useEffect } from 'react';
import { Droppable } from '@hello-pangea/dnd';
import Card from './Card';
import WordPressSiteConfig from './WordPressSiteConfig';
import { Label, Column, Card as CardType } from '../types/board';
import { supabase } from '../services/supabase';
import { wordpressService } from '../services/wordpress';
import './Swimlane.css';

interface SwimlaneInfo {
  id: string;
  name: string;
  color: string;
  type: 'site' | 'owner' | 'date';
}

interface SwimlaneProps {
  label: SwimlaneInfo;
  columns: { [key: string]: Column };
  columnOrder: string[];
  cards: { [key: string]: CardType };
  labels: { [key: string]: Label };
  selectedOwner: string | null;
  selectedSite: string | null;
  selectedDateRange: string | null;
  swimlaneMode: 'site' | 'owner' | 'date';
  onDeleteCard: (cardId: string) => void;
  owners: string[];
  onUpdateOwner: (cardId: string, newOwner: string) => void;
  onUpdateDueDate: (cardId: string, newDueDate: string) => void;
  onUpdateLocalTitle: (cardId: string, newLocalTitle: string) => void;
  onUpdateLocalPlan: (cardId: string, newLocalPlan: string) => void;
  onSyncComplete?: () => void;
}

const Swimlane: React.FC<SwimlaneProps> = ({
  label,
  columns,
  columnOrder,
  cards,
  labels,
  selectedOwner,
  selectedSite,
  selectedDateRange,
  swimlaneMode,
  onDeleteCard,
  owners,
  onUpdateOwner,
  onUpdateDueDate,
  onUpdateLocalTitle,
  onUpdateLocalPlan,
  onSyncComplete,
}) => {
  const envSiteLabel = process.env.REACT_APP_WP_SITE_LABEL; // From .env.local

  // Check localStorage for override, fallback to env variable
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [currentSiteLabel, setCurrentSiteLabel] = useState<string>(() => {
    const stored = localStorage.getItem('wp_site_label');
    return stored || envSiteLabel || '';
  });

  // State for WordPress URL from database
  const [wordpressUrl, setWordpressUrl] = useState<string | null>(null);
  const [wpUrlLoading, setWpUrlLoading] = useState(true);

  // State for syncing
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  // Fetch WordPress URL from database for this specific site
  useEffect(() => {
    const fetchWordPressSite = async () => {
      if (swimlaneMode !== 'site') {
        setWpUrlLoading(false);
        return;
      }

      setWpUrlLoading(true);

      try {
        const { data, error } = await supabase
          .from('wordpress_sites')
          .select('wordpress_url, is_active')
          .eq('label_name', label.name)
          .eq('is_active', true)
          .maybeSingle();

        if (error) {
          console.error('Error fetching WordPress site URL:', error);
          setWordpressUrl(null);
          setWpUrlLoading(false);
          return;
        }

        if (data?.wordpress_url) {
          setWordpressUrl(data.wordpress_url);
        } else {
          setWordpressUrl(null);
        }
      } catch (err) {
        console.error('Failed to fetch WordPress site URL:', err);
        setWordpressUrl(null);
      } finally {
        setWpUrlLoading(false);
      }
    };

    fetchWordPressSite();
  }, [label.name, swimlaneMode]);

  const wordpressAdminUrl = wordpressUrl ? `${wordpressUrl}/wp-admin/edit.php` : null;

  // Check if this specific site has WordPress configured
  const hasWordPressConfigured = !!wordpressUrl;

  // Only show "not configured" styling if we've finished loading and there's no URL
  const showNotConfigured = !wpUrlLoading && !hasWordPressConfigured;

  // Handle WordPress sync for this specific site
  const handleSync = async () => {
    if (!hasWordPressConfigured) {
      alert(`This site (${label.name}) does not have a WordPress URL configured.\n\nClick the "⚙️ Configure" button and then click the "➕" button to add a WordPress URL.`);
      return;
    }

    setIsSyncing(true);
    setSyncMessage('Syncing...');

    try {
      const result = await wordpressService.syncPostsToDatabase(label.name);

      if (result.success) {
        const message = `Synced ${result.postsSynced} posts${result.cardsCreated ? `, created ${result.cardsCreated} cards` : ''}`;
        setSyncMessage(message);

        if (onSyncComplete) {
          onSyncComplete();
        }

        // Clear message after 3 seconds
        setTimeout(() => setSyncMessage(null), 3000);
      } else {
        setSyncMessage(`Sync failed: ${result.error}`);
        setTimeout(() => setSyncMessage(null), 5000);
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      setSyncMessage(`Error: ${errorMsg}`);
      setTimeout(() => setSyncMessage(null), 5000);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleWordPressLinkClick = (e: React.MouseEvent) => {
    if (!hasWordPressConfigured) {
      e.preventDefault();
      alert(`This site (${label.name}) does not have a WordPress URL configured.\n\nClick the "⚙️ Configure" button and then click the "➕" button next to "${label.name}" to add a WordPress URL.`);
    }
  };

  const handleSaveSiteConfig = async (newSiteLabel: string) => {
    localStorage.setItem('wp_site_label', newSiteLabel);
    setCurrentSiteLabel(newSiteLabel);

    // Reload WordPress URL for this site if it matches the new label
    if (label.name === newSiteLabel) {
      try {
        const { data } = await supabase
          .from('wordpress_sites')
          .select('wordpress_url')
          .eq('label_name', newSiteLabel)
          .eq('is_active', true)
          .maybeSingle();

        if (data?.wordpress_url) {
          setWordpressUrl(data.wordpress_url);
        }
      } catch (err) {
        console.error('Failed to reload WordPress URL:', err);
      }
    }

    // Show success message
    alert(`WordPress is now linked to "${newSiteLabel}".\n\nNext time you sync, posts will be associated with this site.`);
  };

  const filterCardsBySwimlane = (card: CardType, swimlaneId: string) => {
    if (swimlaneMode === 'site') {
      return card.labelIds.includes(swimlaneId);
    } else if (swimlaneMode === 'owner') {
      const ownerName = swimlaneId.replace('owner-', '');
      return card.owner === ownerName;
    } else {
      // Date mode
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (!card.dueDate) {
        return swimlaneId === 'date-nodate';
      }

      const dueDate = new Date(card.dueDate);
      dueDate.setHours(0, 0, 0, 0);
      const diffTime = dueDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (swimlaneId === 'date-overdue') return diffDays < 0;
      if (swimlaneId === 'date-today') return diffDays === 0;
      if (swimlaneId === 'date-thisweek') return diffDays > 0 && diffDays <= 7;
      if (swimlaneId === 'date-later') return diffDays > 7;
      return false;
    }
  };

  const matchesDateFilter = (card: CardType, dateRangeId: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!card.dueDate) {
      return dateRangeId === 'date-nodate';
    }

    const dueDate = new Date(card.dueDate);
    dueDate.setHours(0, 0, 0, 0);
    const diffTime = dueDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (dateRangeId === 'date-overdue') return diffDays < 0;
    if (dateRangeId === 'date-today') return diffDays === 0;
    if (dateRangeId === 'date-thisweek') return diffDays > 0 && diffDays <= 7;
    if (dateRangeId === 'date-later') return diffDays > 7;
    return false;
  };

  return (
    <div className="swimlane">
      <div className="swimlane-header">
        <div className="swimlane-header-left">
          <span
            className="swimlane-label-dot"
            style={{ backgroundColor: label.color }}
          ></span>
          <h3>{label.name}</h3>
        </div>
        {swimlaneMode === 'site' && (
          <div className="swimlane-wp-controls">
            <>
              <a
                href={wordpressAdminUrl || '#'}
                target={hasWordPressConfigured ? '_blank' : '_self'}
                rel="noopener noreferrer"
                className={`swimlane-wp-link ${showNotConfigured ? 'not-configured' : ''}`}
                title={
                  hasWordPressConfigured
                    ? 'Open WordPress Admin Posts'
                    : 'This site is not configured with WordPress'
                }
                onClick={handleWordPressLinkClick}
              >
                <span className="wp-icon">🔗</span>
                <span className="wp-link-text">WP Admin</span>
              </a>
              <button
                className={`swimlane-wp-sync ${showNotConfigured ? 'not-configured' : ''}`}
                onClick={handleSync}
                disabled={isSyncing || !hasWordPressConfigured}
                title={hasWordPressConfigured ? 'Sync WordPress posts for this site' : 'Configure WordPress URL first'}
              >
                <span className="sync-icon">{isSyncing ? '⏳' : '🔄'}</span>
                <span className="sync-text">{isSyncing ? 'Syncing...' : 'Sync'}</span>
              </button>
            </>
            <button
              className="swimlane-wp-config"
              onClick={() => setShowConfigModal(true)}
              title="Configure WordPress site association"
            >
              <span className="config-icon">⚙️</span>
              <span className="config-text">Configure</span>
            </button>
            {syncMessage && (
              <span className="swimlane-sync-message">{syncMessage}</span>
            )}
          </div>
        )}
        {showConfigModal && (
          <WordPressSiteConfig
            allLabels={labels}
            currentSiteLabel={currentSiteLabel}
            onClose={async () => {
              setShowConfigModal(false);
              // Reload WordPress URL in case it was updated
              try {
                const { data } = await supabase
                  .from('wordpress_sites')
                  .select('wordpress_url')
                  .eq('label_name', label.name)
                  .eq('is_active', true)
                  .maybeSingle();

                if (data?.wordpress_url) {
                  setWordpressUrl(data.wordpress_url);
                }
              } catch (err) {
                console.error('Failed to reload WordPress URL:', err);
              }
            }}
            onSave={handleSaveSiteConfig}
          />
        )}
      </div>
      <div className="swimlane-columns">
        {columnOrder.map((columnId) => {
          const column = columns[columnId];
          // Filter cards based on swimlane mode and cross-filters
          const swimlaneCards = column.cardIds
            .map((cardId) => cards[cardId])
            .filter((card) => {
              if (!card) return false;

              // Only show WordPress-synced cards (cards with wordpress_post_id)
              if (!card.wordpressPostId) return false;

              // Must match the swimlane
              if (!filterCardsBySwimlane(card, label.id)) return false;

              // Apply cross-filters
              // Filter by owner (when not in owner mode)
              if (selectedOwner && swimlaneMode !== 'owner' && card.owner !== selectedOwner) {
                return false;
              }

              // Filter by site (when not in site mode)
              if (selectedSite && swimlaneMode !== 'site' && !card.labelIds.includes(selectedSite)) {
                return false;
              }

              // Filter by date range (when not in date mode)
              if (selectedDateRange && swimlaneMode !== 'date' && !matchesDateFilter(card, selectedDateRange)) {
                return false;
              }

              return true;
            });

          return (
            <div key={columnId} className="swimlane-column">
              <div className="swimlane-column-header">
                <h4>{column.title}</h4>
                <span className="swimlane-column-count">{swimlaneCards.length}</span>
              </div>
              <Droppable droppableId={`${label.id}-${columnId}`} isDropDisabled={true}>
                {(provided, snapshot) => (
                  <div
                    className="swimlane-column-cards read-only"
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {swimlaneCards.map((card, index) => (
                      <Card
                        key={card.id}
                        card={card}
                        index={index}
                        labels={labels}
                        onDelete={onDeleteCard}
                        owners={owners}
                        onUpdateOwner={onUpdateOwner}
                        onUpdateDueDate={onUpdateDueDate}
                        onUpdateLocalTitle={onUpdateLocalTitle}
                        onUpdateLocalPlan={onUpdateLocalPlan}
                      />
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Swimlane;
